import type { SessionResponse } from '@lms/api-client/backend';
import { AlertTriangle, LoaderCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { saveSession } from '@/features/scormplayer/scorm-api';

declare global {
  interface Window {
    API: any;
    API_1484_11: any;
  }
}

export default function StateBanner({
  session,
}: {
  session?: SessionResponse;
}) {
  const t = useTranslations('player');
  const dirty = useRef(0);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const saveState = useCallback(async () => {
    if (savingRef.current) return; // Skip if a save is in progress
    const dirtyWhenSaving = dirty.current;
    if (session && window.API_1484_11 && dirtyWhenSaving > 0) {
      // Only show spinner if it takes >1s
      const delayedSpinner = setTimeout(() => {
        setSaving(true);
      }, 100);
      savingRef.current = true;
      await saveSession(
        session.id,
        session.personalCode,
        window.API_1484_11.cmi.toJSON(),
      )
        .then(() => {
          if (dirtyWhenSaving === dirty.current) {
            dirty.current = 0;
          }
          setError(undefined);
        })
        .catch((e) => {
          setError(e);
        })
        .finally(() => {
          clearTimeout(delayedSpinner);
          setSaving(false);
          savingRef.current = false;
        });
    }
  }, [session]);

  useEffect(() => {
    const timeout = setInterval(saveState, 10000);
    return () => clearInterval(timeout);
  }, [saveState]);

  useEffect(() => {
    // Mark CMI as "unsaved"
    window.API_1484_11.on('SetValue', function setValueListener() {
      // Even if session time continues ticking after course completion, we don't need to save it.
      // Once we're completed. Stop recording changes.
      if (window.API_1484_11.cmi.completion_status !== 'completed') {
        dirty.current = dirty.current + 1;
      }
    });

    window.API_1484_11.on('Commit', function commitListener() {
      saveState();
    });

    window.API_1484_11.on('Terminate', function terminateListener() {
      saveState();
    });
  }, [saveState]);

  return (
    <div className="slide-in-from-bottom-full absolute right-2 bottom-2 flex h-fit w-fit flex-col items-end justify-end gap-2 transition-all">
      {error ? (
        <div className="bg-destructive text-destructive-foreground slide-in-from-bottom-full flex flex-row items-center gap-2 rounded-md border p-4 shadow-lg transition-all">
          {saving ? (
            <LoaderCircleIcon className="animate-spin" />
          ) : (
            <AlertTriangle />
          )}
          <div>
            <div className="text-sm font-semibold">{t('error')}</div>
            <div className="text-sm opacity-90">{t('error_warning')}</div>
            <div className="text-sm opacity-90">{`${error}`}</div>
          </div>
        </div>
      ) : saving ? (
        <div className="bg-background rounded-md border p-4 shadow-lg">
          <LoaderCircleIcon className="animate-spin" />
        </div>
      ) : undefined}
    </div>
  );
}
